import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Save, Calendar, Check } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { updateVideo, getVideoById, getUserCategories, CategoryData, VideoData, stringToTimestamp, timestampToString, getAuthors, AuthorData } from '@/lib/firebase';
import CalendarModal from '@/components/calendar-modal';
import { logger } from '@/lib/logger';

export default function EditVideo() {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [videoData, setVideoData] = useState<VideoData | null>(null);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [contributors, setContributors] = useState<AuthorData[]>([]);
  const [selectedContributorIds, setSelectedContributorIds] = useState<string[]>([]);
  
  // Get video ID and return params from URL
  const urlParams = new URLSearchParams(window.location.search);
  const videoId = urlParams.get('id');
  const returnParams = urlParams.get('return') || '';
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    publishDate: new Date().toISOString().split('T')[0],
    thumbnailUrl: '',
    contentType: 'Information',
    selectedCategories: [] as string[]
  });

  useEffect(() => {
    if (user && videoId) {
      loadData();
    }
  }, [user, videoId]);

  const loadData = async () => {
    if (!user || !videoId) return;
    
    try {
      setLoading(true);
      const [videoData, categoriesData, contributorsData] = await Promise.all([
        getVideoById(videoId),
        getUserCategories(),
        getAuthors()
      ]);

      setContributors(contributorsData);
      
      if (videoData) {
        setVideoData(videoData);
        setSelectedContributorIds((videoData as any).contributorIds || []);
        setFormData({
          title: videoData.title || '',
          description: videoData.description || '',
          publishDate: videoData.publishDate ? timestampToString(videoData.publishDate) : new Date().toISOString().split('T')[0],
          thumbnailUrl: videoData.thumbnailUrl || '',
          contentType: videoData.contentType || 'Information',
          selectedCategories: videoData.contentCategories || []
        });
      }
      
      setCategories(categoriesData);
    } catch (error) {
      logger.error('Error loading data:', error);
      toast({
        title: "Error loading video",
        description: "Failed to load video data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCategoryToggle = (categoryTag: string) => {
    setFormData(prev => ({
      ...prev,
      selectedCategories: prev.selectedCategories.includes(categoryTag)
        ? prev.selectedCategories.filter(tag => tag !== categoryTag)
        : [...prev.selectedCategories, categoryTag]
    }));
  };

  const filteredCategories = categories.filter(category => {
    if (moduleFilter === 'all') return true;
    return category.module?.toLowerCase() === moduleFilter;
  });

  const getModuleColors = (module: string) => {
    switch (module?.toLowerCase()) {
      case 'body':
        return 'bg-[#d059c4] hover:bg-[#d059c4]/80';
      case 'mind':
        return 'bg-[#66cee7] hover:bg-[#66cee7]/80';
      case 'general':
        return 'bg-[#595ebe] hover:bg-[#595ebe]/80';
      default:
        return 'bg-slate-500 hover:bg-slate-600';
    }
  };

  const handleSave = async (newStatus?: string) => {
    if (!user || !videoId) return;

    if (!formData.title.trim()) {
      toast({
        title: "Title required",
        description: "Please enter a title for the video.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);

    try {
      // Get selected categories data for modules and libraries
      const selectedCategoryData = categories.filter(cat => 
        formData.selectedCategories.includes(cat.tag)
      );
      
      const modules = Array.from(new Set(selectedCategoryData.map(cat => cat.module?.toLowerCase()).filter(Boolean)));
      const contentLibraries = Array.from(new Set(selectedCategoryData.map(cat => cat.contentLibrary).filter(Boolean)));
      
      logger.log('Selected categories:', selectedCategoryData.map(cat => ({ tag: cat.tag, contentLibrary: cat.contentLibrary })));
      logger.log('Computed contentLibraries (should be distinct):', contentLibraries);

      const selectedContributorObjects = contributors.filter(c => selectedContributorIds.includes(c.id || ''));
      const contributorIds = selectedContributorObjects.map(c => c.id!);
      const contributorsPayload = selectedContributorObjects.map(c => ({
        id: c.id!,
        name: c.name,
        profilePicUrl: c.profilePicUrl || '',
        qualifications: c.qualifications || '',
        story: c.story || '',
      }));

      const updates = {
        title: formData.title,
        contributorIds,
        contributors: contributorsPayload,
        description: formData.description,
        publishDate: stringToTimestamp(formData.publishDate),
        contentType: formData.contentType,
        contentCategories: formData.selectedCategories,
        modules,
        contentLibraries,
        ...(formData.thumbnailUrl && { thumbnailUrl: formData.thumbnailUrl }),
        ...(newStatus && { status: newStatus as "Draft" | "Scheduled" | "Published" | "Blocked" })
      };

      await updateVideo(videoId, updates);

      toast({
        title: "Video updated",
        description: `"${formData.title}" has been updated successfully.${newStatus ? ` Status changed to ${newStatus}.` : ''}`,
      });

      // Navigate back with preserved filter/pagination state
      setLocation(`/${returnParams}`);
    } catch (error: any) {
      toast({
        title: "Failed to update video",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    // Validate requirements for publishing
    if (!formData.title.trim()) {
      toast({
        title: "Title required",
        description: "Please enter a title for the video.",
        variant: "destructive",
      });
      return;
    }

    if (formData.selectedCategories.length === 0) {
      toast({
        title: "Category required",
        description: "Please select at least one category.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.contentType) {
      toast({
        title: "Content type required",
        description: "Please select a content type.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.publishDate) {
      toast({
        title: "Publish date required",
        description: "Please select a valid publish date.",
        variant: "destructive",
      });
      return;
    }

    await handleSave('Published');
  };

  const canPublish = () => {
    return formData.title.trim() && 
           formData.selectedCategories.length > 0 && 
           formData.contentType && 
           formData.publishDate;
  };

  const getEffectiveStatus = (video: VideoData) => {
    if (video.status === 'Published' && video.publishDate) {
      const publishDateString = typeof video.publishDate === 'string' ? video.publishDate : timestampToString(video.publishDate);
      const publishDate = new Date(publishDateString);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      publishDate.setHours(0, 0, 0, 0);
      
      if (publishDate > today) {
        return 'Scheduled';
      }
    }
    return video.status;
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Published':
        return 'default';
      case 'Scheduled':
        return 'secondary';
      case 'Draft':
        return 'outline';
      case 'Blocked':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-600">Loading video...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!videoId) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <p className="text-slate-600">Video ID not found.</p>
            <Button onClick={() => setLocation(`/${returnParams}`)} className="mt-4">
              Back to Videos
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation(`/${returnParams}`)}
              className="flex items-center space-x-2"
              data-testid="button-back"
            >
              <ArrowLeft size={16} />
              <span>Back to Videos</span>
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Edit Video</h1>
              <p className="text-slate-600">Update video details and categories</p>
            </div>
          </div>
          {videoData?.status === 'Blocked' ? (
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => handleSave()}
                disabled={saving}
                variant="outline"
                className="flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-slate-600 border-t-transparent rounded-full animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>Save as Blocked</span>
                  </>
                )}
              </Button>
              <Button
                onClick={handlePublish}
                disabled={saving || !canPublish()}
                className="flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Publishing...</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>Publish</span>
                  </>
                )}
              </Button>
            </div>
          ) : videoData?.status === 'Published' || videoData?.status === 'Scheduled' ? (
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => handleSave('Blocked')}
                disabled={saving}
                variant="destructive"
                className="flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Blocking...</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>Block Video</span>
                  </>
                )}
              </Button>
              <Button
                onClick={() => handleSave()}
                disabled={saving}
                className="flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>Update Video</span>
                  </>
                )}
              </Button>
            </div>
          ) : videoData?.status === 'Draft' ? (
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => handleSave()}
                disabled={saving}
                variant="outline"
                className="flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-slate-600 border-t-transparent rounded-full animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>Save as Draft</span>
                  </>
                )}
              </Button>
              <Button
                onClick={() => handleSave('Blocked')}
                disabled={saving}
                variant="destructive"
                className="flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Blocking...</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>Block</span>
                  </>
                )}
              </Button>
              <Button
                onClick={handlePublish}
                disabled={saving || !canPublish()}
                className="flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Publishing...</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>Publish</span>
                  </>
                )}
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => handleSave()}
              disabled={saving}
              className="flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>Update Video</span>
                </>
              )}
            </Button>
          )}
        </div>

        <div className="space-y-6">
          {/* Video Details */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Video Details</CardTitle>
                {videoData?.status && (
                  <Badge 
                    variant={getStatusBadgeVariant(getEffectiveStatus(videoData))} 
                    className="text-sm px-3 py-1"
                  >
                    {getEffectiveStatus(videoData)}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter video title"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Contributors</Label>
                  <div className="mt-2">
                    {contributors.length === 0 ? (
                      <p className="text-sm text-slate-500">No contributors found.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {contributors.map((contributor) => {
                          const isSelected = selectedContributorIds.includes(contributor.id || '');
                          return (
                            <button
                              key={contributor.id}
                              type="button"
                              onClick={() =>
                                setSelectedContributorIds(prev =>
                                  prev.includes(contributor.id || '')
                                    ? prev.filter(id => id !== contributor.id)
                                    : [...prev, contributor.id || '']
                                )
                              }
                              className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-colors text-sm font-medium ${
                                isSelected
                                  ? 'bg-primary text-white border-primary'
                                  : 'bg-white text-slate-700 border-slate-200 hover:border-primary hover:bg-primary/5'
                              }`}
                            >
                              {contributor.profilePicUrl ? (
                                <img
                                  src={contributor.profilePicUrl}
                                  alt={contributor.name}
                                  className="w-6 h-6 rounded-full object-cover"
                                />
                              ) : (
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isSelected ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'}`}>
                                  {contributor.name.charAt(0).toUpperCase()}
                                </div>
                              )}
                              <span>{contributor.name}</span>
                              {isSelected && <Check size={14} />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter video description"
                  className="mt-1"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contentType">Content Type</Label>
                  <Select 
                    value={formData.contentType} 
                    onValueChange={(value) => handleInputChange('contentType', value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Information">Information</SelectItem>
                      <SelectItem value="Instructional">Instructional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Publish Date</Label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCalendarModalOpen(true)}
                    className="w-full mt-1 justify-start text-left font-normal"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {formData.publishDate ? 
                      new Date(formData.publishDate).toLocaleDateString() : 
                      'Select publish date'
                    }
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="thumbnailUrl">Thumbnail URL</Label>
                <Input
                  id="thumbnailUrl"
                  value={formData.thumbnailUrl}
                  onChange={(e) => handleInputChange('thumbnailUrl', e.target.value)}
                  placeholder="https://example.com/thumbnail.jpg"
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* Category Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span>Select Categories</span>
                  <span className="text-sm font-normal text-slate-500">(at least one required)</span>
                </div>
                {/* Module Filter Chips */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setModuleFilter('all')}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                      moduleFilter === 'all'
                        ? 'bg-slate-600 text-white'
                        : 'bg-slate-100 text-slate-600 border border-slate-300 hover:bg-slate-200'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setModuleFilter('body')}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                      moduleFilter === 'body'
                        ? 'bg-[#d059c4] text-white'
                        : 'bg-[#d059c4]/10 text-[#d059c4] border border-[#d059c4] hover:bg-[#d059c4]/20'
                    }`}
                  >
                    body
                  </button>
                  <button
                    onClick={() => setModuleFilter('mind')}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                      moduleFilter === 'mind'
                        ? 'bg-[#66cee7] text-white'
                        : 'bg-[#66cee7]/10 text-[#66cee7] border border-[#66cee7] hover:bg-[#66cee7]/20'
                    }`}
                  >
                    mind
                  </button>
                  <button
                    onClick={() => setModuleFilter('general')}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                      moduleFilter === 'general'
                        ? 'bg-[#595ebe] text-white'
                        : 'bg-[#595ebe]/10 text-[#595ebe] border border-[#595ebe] hover:bg-[#595ebe]/20'
                    }`}
                  >
                    general
                  </button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">

              {/* Category Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 max-h-96 overflow-y-auto">
                {filteredCategories.map((category) => {
                  const colors = getModuleColors(category.module);
                  const isSelected = formData.selectedCategories.includes(category.tag);
                  
                  return (
                    <div 
                      key={category.id} 
                      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        isSelected 
                          ? `border-primary bg-primary/10` 
                          : `border-slate-200 hover:border-slate-300`
                      }`}
                      onClick={() => handleCategoryToggle(category.tag)}
                      style={{
                        backgroundColor: isSelected ? 
                          (category.module?.toLowerCase() === 'body' ? '#d059c4' + '20' :
                           category.module?.toLowerCase() === 'mind' ? '#66cee7' + '20' :
                           category.module?.toLowerCase() === 'general' ? '#595ebe' + '20' : 
                           '#e2e8f0'
                          ) : undefined
                      }}
                    >
                      <div className="flex items-start space-x-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleCategoryToggle(category.tag)}
                          className="mt-0.5 rounded border-slate-300"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900 text-sm leading-tight">
                            {category.title}
                          </div>
                          <div className="text-xs text-slate-600 leading-tight">
                            {category.contentLibrary} - {category.module}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Selected Categories Summary */}
              {formData.selectedCategories.length > 0 && (
                <div className="pt-3 border-t border-slate-200">
                  <Label className="block text-sm font-medium text-slate-700 mb-2">
                    Selected Categories ({formData.selectedCategories.length})
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {formData.selectedCategories.map(tag => {
                      const category = categories.find(c => c.tag === tag);
                      if (!category) return null;
                      
                      return (
                        <Badge 
                          key={tag} 
                          variant="secondary" 
                          className="text-xs"
                        >
                          {category.title}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Calendar Modal */}
        <CalendarModal
          isOpen={calendarModalOpen}
          onClose={() => setCalendarModalOpen(false)}
          onDateSelect={(date) => {
            handleInputChange('publishDate', date);
            setCalendarModalOpen(false);
          }}
          selectedDate={formData.publishDate}
        />
      </div>
    </div>
  );
}